import { BlockScroll, ModalClose, ModalFooter } from "@birdwatch/ui";
import { useUser } from "@clerk/clerk-react";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useCallback, useRef, useState } from "react";
import { Button } from "@/components/ui/button/button";
import { BwLoadingLine } from "@/components/ui/icons";
import { Skeleton } from "@/components/ui/skeleton";
import { PaymentMethodType } from "@/features/shared/billing/payments/_types/payment-method-type";

/** Data returned after a payment method is successfully added or edited. */
export type SuccessData = {
  /** The Stripe payment method ID. */
  paymentMethodId: string;

  /** Whether the payment method was added or edited. */
  mode: "add" | "edit";
};

type PaymentMethodFormContentProps = {
  /** Whether the form is adding a new or editing an existing payment method. */
  mode: "add" | "edit";

  /** The Stripe SetupIntent client secret. */
  clientSecret: string;

  /** Which payment method tab to show first (card or bank account). */
  defaultMethod: PaymentMethodType;

  /** Callback with the new payment method details after successful setup. */
  onSuccess: (data: SuccessData) => Promise<void> | void;

  /**
   * Callback for a setup failure — a declined card, or an unexpected throw.
   *
   * Optional and additive: without it the error is console-logged exactly as before. It
   * exists because a caller cannot otherwise observe a failure at all, and the presale
   * funnel reports one as `Presale.PaymentMethodAddFailed`.
   */
  onError?: (error: { message?: string }) => void;
};

/**
 * The Stripe PaymentElement form for adding or editing a payment method. Handles Stripe setup
 * confirmation, fetches the new payment method details on success, and optionally sets it as
 * default for first-time users.
 *
 * @param mode - Whether the form is adding a new or editing an existing payment method.
 * @param clientSecret - The Stripe SetupIntent client secret.
 * @param defaultMethod - Which payment method tab to show first (card or bank account).
 * @param onSuccess - Callback with the new payment method details after successful setup.
 * @param onError - Callback with the failure message when setup does not complete.
 */
export const PaymentMethodFormContent = ({
  mode,
  clientSecret,
  defaultMethod,
  onSuccess,
  onError,
}: PaymentMethodFormContentProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useUser();
  const [isReady, setIsReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (
        !stripe ||
        !elements ||
        !clientSecret ||
        !user?.primaryEmailAddress?.emailAddress
      ) {
        return;
      }

      setIsSubmitting(true);
      try {
        const { error, setupIntent }: any = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/my/settings/account/billing/payments`,
            payment_method_data: {
              billing_details: {
                email: user.primaryEmailAddress.emailAddress,
              },
            },
          },
          redirect: "if_required",
        });

        if (error) {
          console.error("Setup error:", error.message);
          onError?.({ message: error?.message });
          setIsSubmitting(false);
          return;
        }

        if (
          (setupIntent?.status === "succeeded" ||
            setupIntent?.status === "requires_action") &&
          setupIntent.payment_method
        ) {
          const paymentMethodId = setupIntent.payment_method as string;

          await onSuccess({
            paymentMethodId,
            mode,
          });
        }
      } catch (err) {
        console.error(err);
        onError?.({ message: err instanceof Error ? err.message : undefined });
      } finally {
        setIsSubmitting(false);
      }
    },
    [stripe, elements, clientSecret, user, onSuccess, onError],
  );

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      className="grow flex flex-col min-h-0"
    >
      <BlockScroll className="min-h-0 flex-1 px-4 pb-4 pt-1">
        {!isReady && (
          <div className="space-y-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-[184px] w-full" />
          </div>
        )}
        <PaymentElement
          onReady={() => setIsReady(true)}
          options={{
            layout: "tabs",
            paymentMethodOrder:
              defaultMethod === PaymentMethodType.UsBankAccount
                ? ["us_bank_account", "card"]
                : ["card", "us_bank_account"],
            fields: {
              billingDetails: {
                name: "auto",
                email: "never",
                phone: "auto",
                address: "auto",
              },
            },
          }}
        />
      </BlockScroll>

      {isReady && (
        <ModalFooter className="shrink-0">
          <ModalClose asChild>
            <Button
              type="button"
              kind="outline"
              variant="neutral"
              className="transition-[background-color,transform] duration-150 ease-out active:scale-[0.98]"
            >
              Cancel
            </Button>
          </ModalClose>
          <Button
            type="submit"
            kind="solid"
            variant="primary"
            disabled={!stripe || isSubmitting}
            className="transition-[background-color,transform] duration-150 ease-out active:scale-[0.98]"
          >
            {isSubmitting ? (
              <>
                <BwLoadingLine className="size-5 animate-spin" />
                {mode === "edit" ? "Saving..." : "Adding..."}
              </>
            ) : mode === "edit" ? (
              "Save"
            ) : (
              "Add"
            )}
          </Button>
        </ModalFooter>
      )}
    </form>
  );
};
